<script>
  import PortfolioChrome from './PortfolioChrome.svelte';
  import { onMount } from 'svelte';
  import { isMobile } from '../../stores/screenSize';

  import ClientList from '../client/ClientList.svelte';
  import WorkerList from '../workers/WorkerList.svelte';
  import ProjectList from '../project/ProjectList.svelte';
  import DashboardWorkOrderList from '../workorders/DashboardWorkOrderList.svelte';
  import WorkOrderList from '../workorders/WorkOrderList.svelte';

  import ClientForm from '../client/ClientForm.svelte';
  import WorkerForm from '../workers/WorkerForm.svelte';
  import ProjectForm from '../project/ProjectForm.svelte';
  import WorkOrderForm from '../workorders/WorkOrderForm.svelte';

  import { workOrderStore } from '../../stores/work-order';
  import { projectStore } from '../../stores/project';
  import { clientStore } from '../../stores/client';
  import { worker } from '../../stores/worker';
  import { auth } from '../../stores/auth';
  import { demoMode } from '../../stores/demoMode';

  export let active = 'dashboard';

  let showSidebar = false;
  let form = '';
  let previousAuthState = false;
  let previousDemoState = false;

  // Modal states for actions
  let showModal = null;
  let modalData = {};

  const titles = { dashboard: 'Dashboard', workOrders: 'Work Orders', projects: 'Projects', clients: 'Clients', workers: 'Workers' };
  const formMap = { clients: 'client', workers: 'worker', projects: 'project', workOrders: 'workOrder' };

  $: title = form ? (form === 'workOrder' ? 'New Work Order' : `New ${form.charAt(0).toUpperCase() + form.slice(1)}`) : titles[active];

  $: Component = form === 'client' ? ClientForm
    : form === 'worker' ? WorkerForm
    : form === 'project' ? ProjectForm
    : form === 'workOrder' ? WorkOrderForm
    : active === 'projects' ? ProjectList
    : active === 'clients' ? ClientList
    : active === 'workers' ? WorkerList
    : active === 'workOrders' ? WorkOrderList
    : DashboardWorkOrderList;

  function onNav(e) {
    active = e.detail;
    form = '';
  }

  // Action handlers for component buttons
  function handleAddTask(e) {
    modalData = { projectId: e.detail.projectId };
    form = 'workOrder';
  }

  function handleNewProject(e) {
    modalData = { clientId: e.detail.clientId };
    form = 'project';
  }

  function handleAssignTask(e) {
    modalData = { workerId: e.detail.workerId };
    form = 'workOrder';
  }

  function handleMessage(e) {
    const client = ($clientStore.items || []).find(c => c.id === e.detail.clientId);
    if (client?.email) {
      window.location.href = `mailto:${client.email}?subject=Project Update`;
    }
  }

  function handleSendUpdate(e) {
    const client = ($clientStore.items || []).find(c => c.id === e.detail.clientId);
    if (client?.email) {
      window.location.href = `mailto:${client.email}?subject=Status Update`;
    }
  }

  function handleSchedule(e) {
    // Open calendar or schedule view for worker
    const workerData = ($worker.workers || []).find(w => w.id === e.detail.workerId);
    alert(`Schedule view for ${workerData?.name || 'worker'} coming soon!`);
  }

  function handleLogTime(e) {
    const wo = ($workOrderStore.items || []).find(w => w.id === e.detail.workOrderId);
    const hours = prompt(`Log hours for "${wo?.title || 'work order'}":`, '1');
    if (hours && !isNaN(parseFloat(hours))) {
      workOrderStore.update(e.detail.workOrderId, {
        actual_hours: (wo?.actual_hours || 0) + parseFloat(hours)
      });
    }
  }

  function handleAddNote(e) {
    const wo = ($workOrderStore.items || []).find(w => w.id === e.detail.workOrderId);
    const note = prompt(`Add note to "${wo?.title || 'work order'}":`);
    if (note) {
      const notes = wo?.notes || '';
      workOrderStore.update(e.detail.workOrderId, {
        notes: notes ? `${notes}\n\n${new Date().toLocaleDateString()}: ${note}` : `${new Date().toLocaleDateString()}: ${note}`
      });
    }
  }

  // Reactive: Load data when auth state or demo mode changes to true
  $: {
    const currentAuth = $auth.isAuthenticated;
    const currentDemo = $demoMode;

    // Detect when auth or demo becomes true (and wasn't before)
    if ((currentAuth && !previousAuthState) || (currentDemo && !previousDemoState)) {
      // Clear existing data and reload fresh
      resetAndLoadData();
    }

    previousAuthState = currentAuth;
    previousDemoState = currentDemo;
  }

  function resetAndLoadData() {
    // Reset all stores to clear any stale data
    clientStore.reset();
    worker.reset();
    projectStore.reset();
    workOrderStore.reset();

    // Load fresh data
    loadAllData();
  }

  async function loadAllData() {
    try {
      await Promise.all([
        clientStore.load({}),
        worker.loadWorkers(),
        projectStore.load({}),
        workOrderStore.load({})
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }

  onMount(() => {
    // Load data on mount if already authenticated
    if ($auth.isAuthenticated || $demoMode) {
      loadAllData();
    }
  });
</script>

<div class="layout">
  <PortfolioChrome bind:active bind:showMobile={showSidebar} on:change={onNav} />

  <main>
    <header>
      {#if $isMobile}
        <button class="menu" on:click={() => showSidebar = !showSidebar}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      {/if}
      <h1>{title}</h1>
      <div class="actions">
        {#if form}
          <button class="btn btn-secondary" on:click={() => { form = ''; modalData = {}; }}>Cancel</button>
        {:else if active !== 'dashboard'}
          <button class="btn btn-primary" on:click={() => { modalData = {}; form = formMap[active]; }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M12 4v16m-8-8h16" />
            </svg>
            Add
          </button>
        {/if}
      </div>
    </header>

    <section>
      <svelte:component
        this={Component}
        on:close={() => { form = ''; modalData = {}; }}
        on:addTask={handleAddTask}
        on:newProject={handleNewProject}
        on:assignTask={handleAssignTask}
        on:message={handleMessage}
        on:sendUpdate={handleSendUpdate}
        on:schedule={handleSchedule}
        on:logTime={handleLogTime}
        on:addNote={handleAddNote}
        initialProject={modalData.projectId}
        initialClient={modalData.clientId}
        initialWorker={modalData.workerId}
      />
    </section>
  </main>
</div>

<style>
  .layout {
    min-height: 100vh;
    background: var(--bg);
  }

  main {
    margin-left: 0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  @media (min-width: 768px) {
    main { margin-left: 220px; }
  }

  header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    background: #fff;
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  header h1 {
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .menu {
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    cursor: pointer;
  }

  .menu svg {
    width: 18px;
    height: 18px;
    color: var(--text);
  }

  .actions {
    display: flex;
    gap: 8px;
  }

  section {
    flex: 1;
    padding: 20px;
  }

  @media (max-width: 767px) {
    header { padding: 10px 16px; }
    section { padding: 16px; }
  }
</style>
